import JSONbig from "json-bigint";
import moment from "moment";
import { AuthBase } from "../../../plunk/service/auth-base";
import endpoints from "../../endpoints";
import account from "../../data/account";

/*
 * Bulk email campaigns (BULK_EMAIL_MANAGEMENT_2026-10-02.md §12): the raw
 * authenticated client for /v1/campaign/email/* and /v1/audience.
 *
 * Why not the Service layer: (1) the error body carries the gate code
 * (`{error: "needsReview"|"locked"|…, message}`) and the Service client only
 * keeps `message`; (2) every 64-bit id (audience, person, sender) must travel
 * as a STRING — numbers above 2^53 lose precision in JavaScript (the 2026-10-02
 * smoke turned 7838572516562728911 into …9000 and matched nothing). Responses
 * are parsed with json-bigint in storeAsString mode, so any integer the
 * double can't hold arrives as a string; ids are then passed through
 * `idStr` everywhere and sent back as strings, which the API accepts.
 *
 * Administrator routes (approve/reject, settings PUT, rails connect/
 * set-default, sender set-default) authorize on the ACCESS token's
 * cognito:groups (same finding as views/campaign-review); pass {admin:true}.
 */

const BigJson = JSONbig({
  storeAsString: true
});
export function parseJson(text) {
  return BigJson.parse(text);
}

/** Every id as a string, whatever the transport gave us (number, string, BigNumber). */
export function idStr(v) {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.toFixed === "function") return v.toFixed();
  return String(v);
}
function tenantId() {
  return (account.get() || {}).tenantId || "";
}
async function rawFetch(url, method, body, opts = {}) {
  const bearer = opts.admin ? AuthBase.accessToken || AuthBase.idToken : AuthBase.idToken || AuthBase.accessToken;
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + (bearer || ""),
    "x-app-id": tenantId()
  };
  const res = await fetch(url, {
    method,
    headers,
    mode: "cors",
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  if (res.status === 401 && !opts._retried) {
    try {
      await AuthBase.refreshTokensAsync();
    } catch (e) {/* fall through to the error below */}
    return rawFetch(url, method, body, {
      ...opts,
      _retried: true
    });
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? parseJson(text) : null;
  } catch (e) {
    data = text;
  }
  if (!res.ok) {
    const code = data && typeof data === "object" ? data.error || data.code || null : null;
    const msg = data && typeof data === "object" ? data.message || (typeof data.error === "string" && data.error.length > 24 ? data.error : null) : null;
    const err = new Error(msg || code || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = code;
    err.data = data;
    throw err;
  }
  return data;
}

/** /v1/campaign/email{path} */
export function api(method, path, body, opts) {
  return rawFetch(`${endpoints.product.root}/campaign/email${path}`, method, body, opts);
}

/** /v1/audience{path} (AudienceService) */
export function audienceApi(method, path, body, opts) {
  return rawFetch(`${endpoints.audience.root}/audience${path}`, method, body, opts);
}

/** /v1/content{path} (Content Hub reads; the wizard's Content Hub piece picker) */
export function contentApi(method, path, body, opts) {
  return rawFetch(`${endpoints.product.root}/content${path}`, method, body, opts);
}
export function qs(obj) {
  const parts = [];
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null || v === "" || v === "all") return;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.length ? "?" + parts.join("&") : "";
}

/** Normalize the audience list response ({items}|array) into [{id, name, kind, total}] with string ids. */
export function normalizeLists(d) {
  const arr = Array.isArray(d) ? d : d && (d.items || d.data) || [];
  return arr.map(a => ({
    id: idStr(a.id),
    name: a.name || a.title || idStr(a.id),
    kind: a.kind || (a.audienceType != null ? String(a.audienceType).toLowerCase() : "static"),
    total: Number(a.total) || 0,
    provider: a.provider || null
  }));
}

// ---------------------------------------------------------------- error text

const GATE_TEXT = {
  needsReview: "This workspace requires an Administrator to approve campaigns before they can be scheduled. Submit it for review first.",
  needsTestSend: "Send a test first: the Review step needs a delivered test send before Submit or Schedule.",
  lintFailed: "The checks found errors. Fix them in Content, then run the checks again.",
  senderUnverified: "The sender's domain is not verified. Authenticate it under Senders before this campaign can leave the Plunk rail.",
  invalidTransition: "That action is not available from the campaign's current status.",
  locked: "The campaign is locked while scheduled. Cancel (unschedule) or duplicate it to make changes.",
  notDeletable: "Only Draft or Canceled campaigns can be deleted.",
  notConnected: "Connect the rail first.",
  notConfigured: null,
  // verbatim
  sendgridScope: null,
  // verbatim
  notImplemented: null,
  // verbatim
  resendNotAvailable: null,
  estimateFailed: null,
  notificationRefused: null,
  notificationUnreachable: null
};

/** The inline message for an API error: gate codes map to copy, verbatim codes keep the server's message. */
export function errMsg(e, fallback) {
  if (!e) return fallback || "Something went wrong.";
  const code = e.code;
  if (code && Object.prototype.hasOwnProperty.call(GATE_TEXT, code)) {
    const t = GATE_TEXT[code];
    if (t) return t;
    return e.message || fallback || code;
  }
  if (typeof e.message === "string" && e.message && e.message.length < 300 && !/^HTTP \d+$/.test(e.message)) return e.message;
  if (e.status === 403) return "You don't have permission for that (Administrator only).";
  if (e.status === 404) return "Not found.";
  return fallback || "Something went wrong.";
}
export function toast(msg) {
  try {
    const t = document.querySelector("paper-toast");
    if (t && t.show) t.show(msg);
  } catch (e) {/* no shell */}
}

// ---------------------------------------------------------------- vocab

export const RAIL_LABEL = {
  plunk: "Plunk",
  mailchimp: "Mailchimp",
  constant_contact: "Constant Contact",
  smartlead: "Smartlead"
};
export const KIND_LABEL = {
  broadcast: "broadcast",
  ab_test: "A/B test",
  recurring: "recurring",
  resend: "resend",
  outreach: "outreach"
};
export const STATUSES = ["Draft", "InReview", "Approved", "Scheduled", "Sending", "Paused", "Sent", "Canceled", "Failed", "Rejected", "Archived"];

// ---- archive / restore / delete rules (EmailCampaignService 1.4.9, 2026-10-05) — mirror the API, the API decides
export const ARCHIVABLE = ["Sent", "Canceled", "Draft", "Failed"];
export function isRecurringTemplate(c) {
  return String((c || {}).kind || "").toLowerCase() === "recurring" && !(c || {}).parentId;
}
/** Sent / Canceled / Draft / Failed; a recurring template also from Scheduled / Approved (archiving stops the recurrence). */
export function canArchive(c) {
  const st = (c || {}).status;
  return ARCHIVABLE.includes(st) || isRecurringTemplate(c) && (st === "Scheduled" || st === "Approved");
}
/** Draft / Canceled delete right away; Sent / Failed / Archived only with the campaign name typed (permanent, stats included). */
export function canDelete(c) {
  return ["Draft", "Canceled", "Sent", "Failed", "Archived"].includes((c || {}).status);
}
export function permanentDelete(c) {
  return ["Sent", "Failed", "Archived"].includes((c || {}).status);
}
/** The status an Archived campaign restores to: GET's archived.from, else settings.archivedFrom (list rows carry settingsJson), else Draft. */
export function archivedFrom(c, archived) {
  if (archived && archived.from) return archived.from;
  const s = c ? parseMaybe(c.settingsJson) : null;
  return s && s.archivedFrom || "Draft";
}
export function railLabel(r) {
  return RAIL_LABEL[String(r || "").toLowerCase()] || r || "—";
}
export function kindLabel(k) {
  return KIND_LABEL[String(k || "").toLowerCase()] || k || "—";
}
export function statusLabel(s) {
  return {
    InReview: "In review"
  }[s] || s || "—";
}
export function statusTone(s) {
  switch (s) {
    case "Sent":
      return "success";
    case "Sending":
    case "Approved":
      return "info";
    case "Paused":
    case "InReview":
      return "warning";
    case "Failed":
    case "Rejected":
      return "danger";
    default:
      return "neutral";
  }
}
export function recipientTone(s) {
  switch (s) {
    case "Delivered":
      return "success";
    case "Bounced":
    case "Complained":
    case "Failed":
    case "Dropped":
      return "danger";
    case "Unsubscribed":
      return "warning";
    case "Sent":
      return "info";
    default:
      return "neutral";
  }
}

// Guard thresholds (§11.3): complaint ≥ 0.10 % or hard bounce ≥ 2 % pause the campaign.
// Deliverability guard thresholds: defaults until refreshGuard() loads the workspace settings (Settings page values).
export const GUARD = {
  complaintPct: 0.10,
  hardBouncePct: 2,
  warnAtPct: 80,
  autoPause: true,
  loaded: false
};
let guardPromise = null;
export function refreshGuard() {
  if (guardPromise) return guardPromise;
  guardPromise = api("GET", "/settings").then(s => {
    const g = s && s.guard || {};
    if (g.complaintRate > 0) GUARD.complaintPct = Number(g.complaintRate);
    if (g.hardBounceRate > 0) GUARD.hardBouncePct = Number(g.hardBounceRate);
    if (g.warnAtPct > 0) GUARD.warnAtPct = Number(g.warnAtPct);
    GUARD.autoPause = g.autoPause !== false;
    GUARD.loaded = true;
    return GUARD;
  }).catch(() => GUARD);
  return guardPromise;
}
/** "hot" = a limit is reached; "warm" = at or past warnAtPct % of a limit. */
export function guardState(complaintRate, hardRate) {
  const c = Number(complaintRate) || 0,
    h = Number(hardRate) || 0;
  if (c >= GUARD.complaintPct || h >= GUARD.hardBouncePct) return "hot";
  const w = GUARD.warnAtPct / 100;
  if (c >= GUARD.complaintPct * w || h >= GUARD.hardBouncePct * w) return "warm";
  return "ok";
}

// ---------------------------------------------------------------- formatting

export function num(v) {
  return (Number(v) || 0).toLocaleString("en-US");
}
export function pct(v, d = 1) {
  const n = Number(v);
  return isFinite(n) ? `${n.toFixed(d)} %` : "—";
}
export function money(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 100000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toLocaleString("en-US", {
    maximumFractionDigits: 0
  })}`;
}
// API datetimes are UTC; MySQL-shaped values arrive without a zone suffix, so treat a bare ISO string as UTC.
export function asDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(v)) return new Date(v + "Z");
  return new Date(v);
}
export function dateTime(v, timeZone) {
  if (!v) return "—";
  const opts = {
    dateStyle: "medium",
    timeStyle: "short"
  };
  if (timeZone) {
    try {
      return asDate(v).toLocaleString([], {
        ...opts,
        timeZone
      });
    } catch (e) {/* unknown zone: fall through */}
  }
  return asDate(v).toLocaleString([], opts);
}
export function dateOnly(v) {
  return v ? asDate(v).toLocaleDateString([], {
    month: "short",
    day: "numeric"
  }) : "—";
}
export function ago(v) {
  if (!v) return "";
  const s = Math.max(0, (Date.now() - asDate(v).getTime()) / 1000);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(v).toLocaleDateString();
}

/** Ribbon delta from a decimal (pct change or points): {pct, dir, unit}. */
export function delta(v, unit = "%") {
  if (v == null || !isFinite(Number(v))) return null;
  const n = Number(v);
  return {
    pct: n,
    dir: n > 0 ? "up" : n < 0 ? "down" : "flat",
    unit: unit === "pp" ? "pp" : "%"
  };
}
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (e) {
    return "UTC";
  }
}

/**
 * Global range (plunk-global-range vocabulary) → the stats service's range
 * grammar: today|yesterday|mtd|ytd|all|Nd|"YYYY-MM-DD,YYYY-MM-DD".
 */
export function rangeToApi(range) {
  const m = moment();
  const span = (a, b) => `${a.format("YYYY-MM-DD")},${b.format("YYYY-MM-DD")}`;
  switch (range) {
    case "today":
      return "today";
    case "yesterday":
      return "yesterday";
    case "last_7_days":
      return "7d";
    case "this_week":
      return span(m.clone().startOf("week"), m);
    case "last_week":
      return span(m.clone().subtract(1, "week").startOf("week"), m.clone().subtract(1, "week").endOf("week"));
    case "this_month":
      return "mtd";
    case "last_month":
      return span(m.clone().subtract(1, "month").startOf("month"), m.clone().subtract(1, "month").endOf("month"));
    case "this_quarter":
      return span(m.clone().startOf("quarter"), m);
    case "last_quarter":
      return span(m.clone().subtract(1, "quarter").startOf("quarter"), m.clone().subtract(1, "quarter").endOf("quarter"));
    case "this_year":
      return "ytd";
    case "last_year":
      return span(m.clone().subtract(1, "year").startOf("year"), m.clone().subtract(1, "year").endOf("year"));
    case "all_time":
      return "all";
    default:
      return "mtd";
  }
}

/** Parse a JSON column that may already be an object. */
export function parseMaybe(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    return parseJson(v);
  } catch (e) {
    return null;
  }
}
export function isAdmin() {
  // owner = the workspace owner (Cognito group "owner"); it outranks administrator everywhere else in the App (toolbar, menu)
  return account.inGroup("administrator") || account.inGroup("root") || account.inGroup("owner");
}